import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useMemo, useRef } from 'react'
import { useGoal } from '@/hooks/useGoal'
import { usePlan } from '@/hooks/usePlan'
import { useAccount } from '@/hooks/useAccount'
import { useCategory } from '@/hooks/useCategory'
import { useProUpgrade } from '@/hooks/useProUpgrade'
import { useItem } from '@/hooks/useItem'
import { useDream } from '@/hooks/useDream'
import { useLanguage } from '@/hooks/useLanguage'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { useLoading } from '@/store/contexts/LoadingContext'
import { t } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import { DreamityPlanResDto, type PlanFrequency } from '@/store/types/plan.types'
import { DreamityAccountResDto } from '@/store/types/account.types'
import CategoryAccordion from '@/components/planner/CategoryAccordion/CategoryAccordion'
import PlanCard from '@/components/planner/PlanCard/PlanCard'
import Modal from '@/components/common/Modal/Modal'
import Checklist from '@/pages/Checklist'
import Button from '@/components/common/Button/Button'
import { Input, Textarea } from '@/components/common/Input/Input'
import Skeleton from '@/components/common/Skeleton/Skeleton'
import EmptyState from '@/components/common/EmptyState/EmptyState'
import PageHeader from '@/components/common/PageHeader/PageHeader'

import { IconClipboard } from '@/components/icons/Icons'
import './GoalDetail.css'

function GoalDetail() {
  const { id, goalId } = useParams<{ id: string; goalId: string }>()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { showToast } = useToastContext()
  const { confirm } = useConfirmContext()
  const { startLoading, stopLoading } = useLoading()
  const { isNormal, handleRestrictionError, showProUpgradeDialog } = useProUpgrade()
  const { goals, fetchGoals, selectedGoal, selectGoal } = useGoal()
  const {
    fetchPlans,
    plans,
    createPlan,
    updatePlan,
    deletePlan,
    fetchPlanById,
    loading: planLoading,
  } = usePlan()
  const { fetchAccounts, accounts, loading: accountLoading, deposit } = useAccount()
  const { fetchSubCategories, fetchMainCategories, mainCategories, subCategories, createSubCategory, deleteSubCategory } = useCategory()
  const { fetchItems, items: allItems } = useItem()
  const { fetchDreamById, selectedDream } = useDream()

  const dreamId = id ?? null
  const currentGoalId = goalId ?? null

  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false)
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false)
  const [isTransferScheduleModalOpen, setIsTransferScheduleModalOpen] = useState(false)
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false)
  const [checklistPlanId, setChecklistPlanId] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<DreamityPlanResDto | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<DreamityAccountResDto | null>(null)
  const [isCreatingPlan, setIsCreatingPlan] = useState(false)
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false)
  const [isFetchingPlan, setIsFetchingPlan] = useState(false)
  const lastRequestedPlanIdRef = useRef<string | null>(null)
  const lastRequestedGoalIdRef = useRef<string | null>(null)
  
  const [transferSchedule, setTransferSchedule] = useState({
    amount: 0,
    frequency: 'MONTHLY' as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY',
    frequencyDay: 1,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  })

  const [createPlanForm, setCreatePlanForm] = useState({
    planName: '',
    planDesc: '',
    frequency: 'DAILY' as PlanFrequency,
    frequencyDay: null as number | null,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isCompleted: false,
  })

  const SUB_CATEGORY_LIMIT_NORMAL = 3
  const [isCreateSubCategoryModalOpen, setIsCreateSubCategoryModalOpen] = useState(false)
  const [isCreatingSubCategory, setIsCreatingSubCategory] = useState(false)
  const [createSubCategoryForm, setCreateSubCategoryForm] = useState({
    mainCategoryId: null as number | null,
    subCategoryName: '',
  })

  // 이미 조회한 planId를 추적하여 중복 호출 방지
  const hasFetchedGoalsRef = useRef<string | null>(null)
  const hasFetchedDreamRef = useRef<string | null>(null)
  const hasFetchedPlansRef = useRef<string | null>(null)

  // 모든 계획의 Item 조회하여 진행률 계산
  useEffect(() => {
    if (dreamId && plans && plans.length > 0) {
      plans.forEach((plan) => {
        if (plan.planId) {
          fetchItems(dreamId, plan.planId).catch(() => {})
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamId, plans])

  useEffect(() => {
    if (!dreamId) return
    // 같은 dreamId에 대해 이미 호출했으면 중복 호출 방지
    if (hasFetchedDreamRef.current === dreamId) {
      return
    }
    
    hasFetchedDreamRef.current = dreamId
    fetchDreamById(dreamId).catch(() => {
      hasFetchedDreamRef.current = null
    })
  }, [dreamId, fetchDreamById])

  useEffect(() => {
    if (!dreamId) return
    const key = `${dreamId}-${language}`
    // 같은 dreamId와 language에 대해 이미 호출했으면 중복 호출 방지
    if (hasFetchedGoalsRef.current === key) {
      return
    }
    
    hasFetchedGoalsRef.current = key
    fetchGoals(dreamId).catch(() => {
      hasFetchedGoalsRef.current = null
    })
    fetchAccounts(dreamId).catch(() => {
      // 에러 처리
    })
    fetchSubCategories(language).catch(() => {
      // 에러 처리
    })
    fetchMainCategories(language).catch(() => {
      // 에러 처리
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dreamId, language])

  useEffect(() => {
    // goals가 업데이트된 후 currentGoalId에 해당하는 goal 찾기
    if (currentGoalId && goals.length > 0) {
      const goal = goals.find((g) => g.goalId === currentGoalId)
      if (goal && (!selectedGoal || selectedGoal.goalId !== currentGoalId)) {
        selectGoal(goal)
        // 같은 goalId에 대해 이미 호출했으면 중복 호출 방지
        if (hasFetchedPlansRef.current !== currentGoalId) {
          hasFetchedPlansRef.current = currentGoalId
          if (dreamId) {
            fetchPlans(dreamId, currentGoalId).catch(() => {
              hasFetchedPlansRef.current = null
            })
          } else {
            hasFetchedPlansRef.current = null
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGoalId, goals])

  // 메뉴 이동 스피너: 계획·계정 로딩 완료 후 종료
  useEffect(() => {
    if (dreamId != null && currentGoalId != null && !planLoading && !accountLoading) {
      stopLoading()
    }
  }, [dreamId, currentGoalId, planLoading, accountLoading, stopLoading])

  // Plan별 진행률 및 아이템 예산 총합 계산
  const planStats = useMemo(() => {
    const stats: Record<string, { progress: number; totalBudget: number }> = {}
    plans.forEach((plan) => {
      if (plan.planId == null) return
      const planItems = allItems.filter((item) => String(item.planId) === String(plan.planId))
      const totalBudget = planItems.reduce((sum, item) => sum + (item.itemPrice || 0), 0)
      
      let progress = 0
      if (planItems.length === 0) {
        progress = plan.isCompleted ? 1 : 0
      } else {
        const completedCount = planItems.filter((item) => item.isCompleted).length
        progress = completedCount / planItems.length
      }
      
      stats[plan.planId] = { progress, totalBudget }
    })
    return stats
  }, [plans, allItems])

  const handlePlanClick = (planId: string) => {
    setChecklistPlanId(planId)
    startLoading()
    setIsChecklistModalOpen(true)
  }

  const handleCreatePlan = async () => {
    if (!currentGoalId) return
    try {
      setIsCreatingPlan(true)
      startLoading()
      await createPlan(dreamId!, {
        goalId: currentGoalId,
        dreamId: dreamId!,
        planName: createPlanForm.planName,
        planDesc: createPlanForm.planDesc,
        frequency: createPlanForm.frequency,
        frequencyDay: createPlanForm.frequencyDay,
        isCompleted: false,
        startDate: createPlanForm.startDate,
        endDate: createPlanForm.endDate,
      })
      
      // createPlan 내부에서 이미 fetchPlans를 호출하므로 추가 호출 불필요
      // 목표 목록도 새로고침 (진행률 등 반영)
      if (dreamId) {
        await fetchGoals(dreamId)
      }
      
      setIsCreatePlanModalOpen(false)
      setCreatePlanForm({
        planName: '',
        planDesc: '',
        frequency: 'DAILY',
        frequencyDay: null,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isCompleted: false,
      })
      showToast(t('messages.createSuccess', language), 'success')
      setIsCreatingPlan(false)
    } catch (error: any) {
      // 에러 발생 시 먼저 스피너를 끄고 나서 다이얼로그 표시
      setIsCreatingPlan(false)
      
      const errorMessage = error?.response?.data?.message || error?.message || t('messages.createFailed', language)
      const handled = await handleRestrictionError(error)
      if (!handled) {
        showToast(errorMessage, 'error')
      }
    } finally {
      stopLoading()
    }
  }

  const handleTogglePlanComplete = async (plan: DreamityPlanResDto) => {
    if (!plan.planId) return
    try {
      if (!plan.isCompleted) {
        const confirmMsg =
          t('goalDetail.completeConfirm', language) ||
          '체크리스트가 모두 완료되지 않았을 수 있습니다. 완료로 표시하시겠습니까?'
        const confirmed = await confirm({ message: confirmMsg })
        if (!confirmed) return
      }

      await updatePlan(dreamId!, plan.planId, {
        planName: plan.planName,
        planDesc: plan.planDesc,
        frequency: plan.frequency,
        frequencyDay: plan.frequencyDay,
        isCompleted: !plan.isCompleted,
        startDate: plan.startDate,
        endDate: plan.endDate,
      })
      if (currentGoalId) {
        fetchPlans(dreamId!, currentGoalId)
      }
    } catch (error) {
      showToast(t('messages.updateFailed', language), 'error')
    }
  }

  const handleEditPlan = (plan: DreamityPlanResDto) => {
    // Determine goalId immediately and fetch plan detail right away
    const planId = plan.planId ?? null
    let goalIdToUse = currentGoalId ?? plan.goalId ?? null
    if (!goalIdToUse) {
      const found = plans.find((p) => p.planId === planId)
      if (found) goalIdToUse = found.goalId ?? null
    }
    
    if (!planId) {
      showToast(t('messages.planSelectRequired', language), 'error')
      return
    }
    if (!dreamId || !goalIdToUse) {
      showToast(t('messages.fetchFailed', language) || '상세 정보를 불러올 수 없습니다.', 'error')
      return
    }

    // useEffect( isEditPlanModalOpen )가 planId로 스냅샷을 조회한다. ref 없이 selectedPlan만 비우면 planId를 잃어 모달이 닫히거나 이전 데이터가 남는다.
    lastRequestedPlanIdRef.current = planId
    lastRequestedGoalIdRef.current = goalIdToUse
    setSelectedPlan(null)
    setIsEditPlanModalOpen(true)
  }

  // 모달이 열릴 때마다 항상 서버에서 상세를 조회하여 렌더한다.
  useEffect(() => {
    if (!isEditPlanModalOpen) return

    const planIdToFetch = lastRequestedPlanIdRef.current ?? selectedPlan?.planId
    const goalIdToUse = lastRequestedGoalIdRef.current ?? currentGoalId ?? selectedPlan?.goalId ?? null
    
    if (!planIdToFetch) {
      console.warn('No planId available for fetching plan detail', { planIdToFetch })
      setIsEditPlanModalOpen(false)
      return
    }
    if (!dreamId || !goalIdToUse) {
      console.warn('No dreamId or goalId available for fetching plan detail', { planIdToFetch, dreamId, goalIdToUse })
      setIsEditPlanModalOpen(false)
      return
    }

    setIsFetchingPlan(true)
    startLoading()
    fetchPlanById(dreamId, goalIdToUse, planIdToFetch)
      .then((res) => {
        if (!res) throw new Error('no plan')
        setSelectedPlan(res)
        setCreatePlanForm({
          planName: res.planName ?? '',
          planDesc: res.planDesc ?? '',
          frequency: res.frequency ?? 'NONE',
          frequencyDay: res.frequencyDay ?? null,
          startDate: res.startDate ?? '',
          endDate: res.endDate ?? '',
          isCompleted: res.isCompleted ?? false,
        })
      })
      .catch((error) => {
        console.error('fetchPlanById failed', error)
        showToast(t('messages.fetchFailed', language) || '불러오기에 실패했습니다.', 'error')
        setIsEditPlanModalOpen(false)
      })
      .finally(() => {
        setIsFetchingPlan(false)
        stopLoading()
        // 요청 id는 한 번 사용 후 초기화
        lastRequestedPlanIdRef.current = null
        lastRequestedGoalIdRef.current = null
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditPlanModalOpen])

  // Debug: log modal open events and relevant ids/state to ensure fetch is triggered
  useEffect(() => {
    // debug effect removed
  }, [isEditPlanModalOpen, selectedPlan, dreamId, currentGoalId])

  // Fallback: document-level click handler for edit buttons (in case prop onEdit isn't reaching)
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement | null
        if (!target) return
        const btn = target.closest('[data-plan-edit]') as HTMLElement | null
        if (!btn) return
        const idAttr = btn.getAttribute('data-plan-edit')
        if (!idAttr) return
        const pid = idAttr
        const found = plans.find((p) => p.planId === pid)
        if (found) {
                  
          handleEditPlan(found)
        }
      } catch (err) {
        console.error('Fallback click handler error', err)
      }
    }

    document.addEventListener('click', onDocClick)
    return () => {
      document.removeEventListener('click', onDocClick)
    }
  }, [plans, handleEditPlan])

  // Global debug click logger: logs clicks and detects plan-edit related clicks (do not change behavior)
  useEffect(() => {
    const debugClick = (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement | null
        if (!target) return
        const text = (target.textContent || '').trim()
        const dataAttr = (target.closest('[data-plan-edit]') as HTMLElement | null)?.getAttribute('data-plan-edit') || null
        const planCardEl = target.closest('.plan-card') as HTMLElement | null
        // Only log when user clicks something that looks like an edit button or plan card
        if (dataAttr || /수정|edit/i.test(text) || planCardEl) {
          
        }
      } catch (err) {
        // ignore
      }
    }
    document.addEventListener('click', debugClick)
    return () => document.removeEventListener('click', debugClick)
  }, [])

  const handleUpdatePlan = async () => {
    if (!selectedPlan) return

    // 폼 검증
    if (!createPlanForm.planName.trim()) {
      showToast(t('goalDetail.planNameRequired', language) || '계획 이름을 입력해주세요.', 'error')
      return
    }

    if (new Date(createPlanForm.startDate) > new Date(createPlanForm.endDate)) {
      showToast(t('messages.invalidDateRange', language) || '시작일은 종료일보다 이전이어야 합니다.', 'error')
      return
    }

    if (createPlanForm.frequency !== 'DAILY' && createPlanForm.frequency !== 'NONE' && !createPlanForm.frequencyDay) {
      showToast(t('messages.frequencyDayRequired', language) || '빈도 일자를 입력해주세요.', 'error')
      return
    }

    try {
      setIsUpdatingPlan(true)
      startLoading()
      // 완료 상태는 기존 상태 유지
      await updatePlan(dreamId!, selectedPlan.planId, {
        planName: createPlanForm.planName.trim(),
        planDesc: createPlanForm.planDesc.trim(),
        frequency: createPlanForm.frequency,
        frequencyDay: createPlanForm.frequencyDay,
        isCompleted: selectedPlan.isCompleted, // 기존 완료 상태 유지
        startDate: createPlanForm.startDate,
        endDate: createPlanForm.endDate,
      })
      
      // updatePlan 내부에서 상태를 업데이트하지만, 목록 갱신을 위해 현재 goal의 plans만 재조회
      if (currentGoalId) {
        await fetchPlans(dreamId!, currentGoalId)
      }
      setIsEditPlanModalOpen(false)
      setSelectedPlan(null)
      showToast(t('messages.updateSuccess', language), 'success')
    } catch (error) {
      showToast(t('messages.updateFailed', language), 'error')
    } finally {
      setIsUpdatingPlan(false)
      stopLoading()
    }
  }

  const handleDeletePlan = async (planId: string) => {
    try {
      const confirmed = await confirm({ 
        message: t('goalDetail.deletePlanConfirm', language),
        variant: 'danger'
      })
      if (confirmed) {
        startLoading()
        await deletePlan(dreamId!, planId)
        await fetchPlans(dreamId!, currentGoalId!)
        // 목표 목록도 새로고침 (진행률 등 반영)
        if (dreamId) {
          await fetchGoals(dreamId)
        }
        showToast(t('messages.deleteSuccess', language), 'success')
      }
    } catch (error) {
      showToast(t('messages.deleteFailed', language), 'error')
    } finally {
      stopLoading()
    }
  }

  const handleOpenCreateSubCategory = async () => {
    if (isNormal && subCategories.length >= SUB_CATEGORY_LIMIT_NORMAL) {
      await showProUpgradeDialog()
      return
    }
    if (mainCategories.length === 0) {
      showToast(t('messages.firstCreateMainCategory', language), 'info')
      return
    }
    setCreateSubCategoryForm({ mainCategoryId: mainCategories[0]?.id ?? null, subCategoryName: '' })
    setIsCreateSubCategoryModalOpen(true)
  }

  const handleCreateSubCategory = async () => {
    const mainId = createSubCategoryForm.mainCategoryId
    const name = createSubCategoryForm.subCategoryName?.trim()
    if (mainId == null || !name) {
      showToast(t('messages.subCategoryRequired', language), 'error')
      return
    }
    try {
      setIsCreatingSubCategory(true)
      startLoading()
      const main = mainCategories.find((m) => m.id === mainId)
      const code = main ? `SUB_${main.mainCategoryCode}_${Date.now()}` : `SUB_${mainId}_${Date.now()}`
      await createSubCategory({
        mainCategoryId: mainId,
        subCategoryCode: code,
        subCategoryNameKo: language === 'ko' ? name : name,
        subCategoryNameEn: language === 'en' ? name : name,
      })
      await fetchSubCategories(language)
      setIsCreateSubCategoryModalOpen(false)
      setCreateSubCategoryForm({ mainCategoryId: null, subCategoryName: '' })
      showToast(t('messages.createSuccess', language), 'success')
    } catch (error: any) {
      const handled = await handleRestrictionError(error)
      if (!handled) {
        showToast(error?.response?.data?.message || error?.message || t('messages.createFailed', language), 'error')
      }
    } finally {
      setIsCreatingSubCategory(false)
      stopLoading()
    }
  }

  const dreamAccounts = accounts.filter(
    (account) => dreamId != null && String(account.dreamId) === dreamId
  )

  // 카테고리별로 계획 그룹화
  // Plan은 Dream의 카테고리를 상속받으므로, Dream의 카테고리별로 그룹화
  // 실제로는 모든 계획을 표시 (Plan 자체에 카테고리 선택 기능 없음)
  const plansByCategory = useMemo(() => {
    const result: Record<number, DreamityPlanResDto[]> = {}
    
    // 각 소분류에 모든 계획을 할당 (Plan은 Dream의 카테고리를 상속받음)
    subCategories.forEach((category) => {
      result[category.id] = plans
    })
    
    // 카테고리가 없으면 "기타" 카테고리로 표시
    if (subCategories.length === 0 && plans.length > 0) {
      result[0] = plans
    }
    
    return result
  }, [plans, subCategories])

  const checklistCloseLockRef = useRef(false)

  const handleChecklistClose = async () => {
    if (checklistCloseLockRef.current) return
    checklistCloseLockRef.current = true
    
    setIsChecklistModalOpen(false)

    // 모달이 닫힐 때 데이터 갱신
    if (dreamId != null && currentGoalId != null) {
      // 1. fetchPlans (update stats/progress)
      fetchPlans(dreamId, currentGoalId).catch(() => {})
      // 2. fetchItems (update budgets)
      if (checklistPlanId) {
        fetchItems(dreamId, checklistPlanId).catch(() => {})
      }
      // 3. fetchDream (update dream card budgets)
      fetchDreamById(dreamId).catch(() => {})
    }
    setChecklistPlanId(null)
    
    setTimeout(() => {
      checklistCloseLockRef.current = false
    }, 300)
  }

  // 아이템 모달이 열린 상태에서 onItemUpdated → fetchPlans 시 planLoading이 true가 되면
  // early return으로 모달이 사라졌다가 다시 나타나며 Checklist가 리마운트되고 fetchItems가 중복 호출됨.
  // 모달이 열려 있을 때는 스켈레톤으로 전환하지 않아 모달을 유지한다.
  if ((planLoading || accountLoading) && !isChecklistModalOpen) {
    return (
      <div className="goal-detail">
        <Skeleton height={40} width={200} style={{ marginBottom: 'var(--spacing-lg)' }} />
        <Skeleton height={300} style={{ width: '100%' }} />
      </div>
    )
  }

  return (
    <div className="goal-detail" style={{ position: 'relative' }}>
      <PageHeader showBack={false} onBack={() => navigate(-1)} />

      <div className="goal-detail__budget">
        <h3>{t('goalDetail.budgetManagement', language)}</h3>
        {dreamAccounts.length > 0 ? (
          <div className="goal-detail__accounts">
            {dreamAccounts.map((account) => (
              <div key={account.accountId} className="goal-detail__account-card">
                <h4>{account.accountName}</h4>
                <p>
                  {t('goalDetail.currentBalance', language)}: {formatCurrency(account.currentBalance, language)}
                </p>
                <p>{t('goalDetail.targetAmount', language)}: {formatCurrency(account.targetAmount, language)}</p>
                <p>
                  {t('goalDetail.budget', language)}: {formatCurrency(account.budget, language)}
                </p>
                <div className="goal-detail__progress">
                  <div
                    className="goal-detail__progress-bar"
                    style={{
                      width: `${
                        (account.currentBalance / account.targetAmount) * 100
                      }%`,
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedAccount(account)
                    setIsTransferScheduleModalOpen(true)
                  }}
                  className="goal-detail__transfer-schedule-btn"
                >
                  {t('goalDetail.setTransferSchedule', language)}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="goal-detail__no-account">{t('goalDetail.noAccount', language)}</p>
        )}
      </div>

      <div className="goal-detail__categories">
        <div className="goal-detail__plan-header">
          <h3>{t('goalDetail.planList', language)}</h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleOpenCreateSubCategory}
            aria-label={language === 'ko' ? '서브 카테고리 추가' : 'Add sub category'}
          >
            {language === 'ko' ? '서브 카테고리 추가' : 'Add sub category'}
          </Button>
        </div>
        {subCategories.length > 0 ? (
          subCategories.map((category) => {
              
              return (
                <CategoryAccordion
                  key={category.id}
                  category={category}
                  plans={(plansByCategory[category.id] || []).map(p => {
                    const planItems = allItems.filter(item => String(item.planId) === String(p.planId))
                    const totalBudget = planItems.reduce((sum, item) => sum + (item.itemPrice || 0), 0)
                    return {
                      ...p,
                      totalItemPrice: totalBudget
                    }
                  })}
                  planProgressRates={Object.keys(planStats).reduce((acc, id) => {
                    acc[id] = planStats[id].progress
                    return acc
                  }, {} as Record<string, number>)}
                  onPlanClick={handlePlanClick}
                  onPlanEdit={handleEditPlan}
                  onPlanDelete={handleDeletePlan}
                  onPlanToggleComplete={handleTogglePlanComplete}
                  onPlanCreate={() => setIsCreatePlanModalOpen(true)}
                  onCategoryEdit={() => {
                    fetchSubCategories(language)
                  }}
                  onCategoryDelete={async (categoryId) => {
                  try {
                    const confirmed = await confirm({ 
                      message: t('category.deleteConfirm', language),
                      variant: 'danger'
                    })
                    if (confirmed) {
                      await deleteSubCategory(categoryId)
                      await fetchSubCategories(language)
                    }
                  } catch (error) {
                    showToast(t('messages.deleteFailed', language), 'error')
                  }
                }}
              />
            )
          })
        ) : (
          <div className="goal-detail__plans-grid">
              {plans.length === 0 ? (
              <EmptyState
                icon={<IconClipboard size={32} />}
                title={t('goalDetail.noPlans', language)}
                description={t('goalDetail.noPlansDesc', language) || '계획을 추가해보세요!'}
              />
            ) : (
              plans.map((plan) => {
                
                return (
                  <PlanCard
                    key={plan.planId}
                    plan={{
                      ...plan,
                      totalItemPrice: planStats[plan.planId]?.totalBudget || 0
                    }}
                    progressRate={planStats[plan.planId]?.progress}
                    onClick={() => handlePlanClick(plan.planId)}
                    onEdit={handleEditPlan}
                    onDelete={handleDeletePlan}
                  />
                )
              })
            )}
          </div>
        )}
      </div>

      {/* 계획 생성 모달 */}
      <Modal
        isOpen={isCreatePlanModalOpen}
        onClose={() => !isCreatingPlan && setIsCreatePlanModalOpen(false)}
        title={t('goalDetail.createPlanTitle', language)}
        size="md"
      >
        <div className={`goal-detail__modal-content ${isCreatingPlan ? 'goal-detail__modal-content--loading' : ''}`}>
          <Input
            label={t('goalDetail.planName', language)}
            value={createPlanForm.planName}
            onChange={(e) =>
              setCreatePlanForm({ ...createPlanForm, planName: e.target.value })
            }
            placeholder={t('goalDetail.planNamePlaceholder', language)}
            required
          />
          <Textarea
            label={t('goalDetail.planDesc', language)}
            value={createPlanForm.planDesc}
            onChange={(e) =>
              setCreatePlanForm({ ...createPlanForm, planDesc: e.target.value })
            }
          />
          <div className="goal-detail__form-row">
            <div className="goal-detail__form-group">
              <label>{t('goalDetail.frequency', language)}</label>
              <select
                value={createPlanForm.frequency}
                onChange={(e) => {
                  const newFrequency = e.target.value as 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
                  if (newFrequency === 'NONE') {
                    // NONE이면 종료일을 시작일과 동일하게 설정
                    setCreatePlanForm({
                      ...createPlanForm,
                      frequency: newFrequency,
                      frequencyDay: null,
                      endDate: createPlanForm.startDate,
                    })
                  } else if (selectedDream) {
                    // DAILY/WEEKLY/MONTHLY이면 꿈의 시작일/종료일을 디폴트로 설정
                    setCreatePlanForm({
                      ...createPlanForm,
                      frequency: newFrequency,
                      frequencyDay: null,
                      startDate: selectedDream.startDate || createPlanForm.startDate,
                      endDate: selectedDream.endDate || createPlanForm.endDate,
                    })
                  } else {
                    setCreatePlanForm({
                      ...createPlanForm,
                      frequency: newFrequency,
                      frequencyDay: null,
                    })
                  }
                }}
              >
                <option value="NONE">{t('plan.frequency.none', language) || '없음'}</option>
                <option value="DAILY">{t('plan.frequency.daily', language)}</option>
                <option value="WEEKLY">{t('plan.frequency.weekly', language)}</option>
                <option value="MONTHLY">{t('plan.frequency.monthly', language)}</option>
              </select>
            </div>
            {createPlanForm.frequency === 'WEEKLY' && (
              <div className="goal-detail__form-group">
                <label>{t('goalDetail.frequencyDay', language) || '요일 선택'}</label>
                <select
                  value={createPlanForm.frequencyDay || ''}
                  onChange={(e) =>
                    setCreatePlanForm({
                      ...createPlanForm,
                      frequencyDay: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  className="goal-detail__select"
                >
                  <option value="">{t('calendar.selectDay', language) || '요일을 선택하세요'}</option>
                  <option value="1">{t('calendar.monday', language) || '월요일'}</option>
                  <option value="2">{t('calendar.tuesday', language) || '화요일'}</option>
                  <option value="3">{t('calendar.wednesday', language) || '수요일'}</option>
                  <option value="4">{t('calendar.thursday', language) || '목요일'}</option>
                  <option value="5">{t('calendar.friday', language) || '금요일'}</option>
                  <option value="6">{t('calendar.saturday', language) || '토요일'}</option>
                  <option value="7">{t('calendar.sunday', language) || '일요일'}</option>
                </select>
              </div>
            )}
            {createPlanForm.frequency === 'MONTHLY' && (
              <div className="goal-detail__form-group">
                <label>{t('goalDetail.frequencyDay', language) || '일 선택'}</label>
                <select
                  value={createPlanForm.frequencyDay || ''}
                  onChange={(e) =>
                    setCreatePlanForm({
                      ...createPlanForm,
                      frequencyDay: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  className="goal-detail__select"
                >
                  <option value="">{t('calendar.selectDay', language) || '일을 선택하세요'}</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}{language === 'ko' ? '일' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="goal-detail__form-row goal-detail__form-row--dates">
            <Input
              label={t('goalDetail.startDate', language)}
              type="date"
              value={createPlanForm.startDate}
              onChange={(e) => {
                const newStartDate = e.target.value
                setCreatePlanForm({
                  ...createPlanForm,
                  startDate: newStartDate,
                  // NONE이면 종료일도 시작일과 동일하게 업데이트
                  endDate: createPlanForm.frequency === 'NONE' ? newStartDate : createPlanForm.endDate,
                })
              }}
            />
            <Input
              label={t('goalDetail.endDate', language)}
              type="date"
              value={createPlanForm.endDate}
              onChange={(e) =>
                setCreatePlanForm({ ...createPlanForm, endDate: e.target.value })
              }
              readOnly={createPlanForm.frequency === 'NONE'}
            />
          </div>

              <div className="goal-detail__modal-actions">
                <Button onClick={handleCreatePlan} disabled={isCreatingPlan}>
                  {isCreatingPlan ? (
                    <>
                      <span className="goal-detail__loading-spinner"></span>
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('common.create', language)
                  )}
                </Button>
              </div>
        </div>
      </Modal>

      {/* 서브 카테고리 생성 모달 */}
      <Modal
        isOpen={isCreateSubCategoryModalOpen}
        onClose={() => !isCreatingSubCategory && setIsCreateSubCategoryModalOpen(false)}
        title={language === 'ko' ? '서브 카테고리 추가' : 'Add sub category'}
        size="sm"
      >
        <div className={`goal-detail__modal-content ${isCreatingSubCategory ? 'goal-detail__modal-content--loading' : ''}`}>
          <div className="goal-detail__form-group">
            <label>{t('dreams.mainCategory', language) || '메인 카테고리'}</label>
            <select
              value={createSubCategoryForm.mainCategoryId ?? ''}
              onChange={(e) =>
                setCreateSubCategoryForm({
                  ...createSubCategoryForm,
                  mainCategoryId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="goal-detail__select"
            >
              <option value="">{t('dreams.selectMainCategory', language) || '메인 카테고리를 선택하세요'}</option>
              {mainCategories.map((main) => (
                <option key={main.id} value={main.id}>
                  {main.mainCategoryName}
                </option>
              ))}
            </select>
          </div>
          <Input
            label={t('dreams.subCategory', language) || '서브 카테고리'}
            value={createSubCategoryForm.subCategoryName}
            onChange={(e) =>
              setCreateSubCategoryForm({ ...createSubCategoryForm, subCategoryName: e.target.value })
            }
            placeholder={language === 'ko' ? '서브 카테고리 이름' : 'Sub category name'}
          />
          <div className="goal-detail__modal-actions">
            <Button variant="primary" onClick={handleCreateSubCategory} disabled={isCreatingSubCategory}>
              {isCreatingSubCategory ? (t('messages.processing', language) || '처리중입니다...') : t('common.create', language)}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 계획 수정 모달 */}
      <Modal
        isOpen={isEditPlanModalOpen}
        onClose={() => {
          if (!isUpdatingPlan && !isFetchingPlan) {
            setIsEditPlanModalOpen(false)
            setSelectedPlan(null)
          }
        }}
        title={t('goalDetail.editPlanTitle', language)}
        size="md"
      >
        <div className={`goal-detail__modal-content ${(isUpdatingPlan || isFetchingPlan) ? 'goal-detail__modal-content--loading' : ''}`}>
          {selectedPlan && (
            <>
              <Input
                label={t('goalDetail.planName', language)}
                value={createPlanForm.planName}
                onChange={(e) =>
                  setCreatePlanForm({ ...createPlanForm, planName: e.target.value })
                }
                required
              />
              <Input
                label={t('goalDetail.planDesc', language)}
                value={createPlanForm.planDesc}
                onChange={(e) =>
                  setCreatePlanForm({ ...createPlanForm, planDesc: e.target.value })
                }
              />
              <div className="goal-detail__form-row">
                <div className="goal-detail__form-group">
                  <label>{t('goalDetail.frequency', language)}</label>
                  <select
                    value={createPlanForm.frequency}
                    onChange={(e) => {
                      const newFrequency = e.target.value as 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
                      if (newFrequency === 'NONE') {
                        // NONE이면 종료일을 시작일과 동일하게 설정
                        setCreatePlanForm({
                          ...createPlanForm,
                          frequency: newFrequency,
                          frequencyDay: null,
                          endDate: createPlanForm.startDate,
                        })
                      } else if (selectedDream) {
                        // DAILY/WEEKLY/MONTHLY이면 꿈의 시작일/종료일을 디폴트로 설정
                        setCreatePlanForm({
                          ...createPlanForm,
                          frequency: newFrequency,
                          frequencyDay: null,
                          startDate: selectedDream.startDate || createPlanForm.startDate,
                          endDate: selectedDream.endDate || createPlanForm.endDate,
                        })
                      } else {
                        setCreatePlanForm({
                          ...createPlanForm,
                          frequency: newFrequency,
                          frequencyDay: null,
                        })
                      }
                    }}
                  >
                    <option value="NONE">{t('plan.frequency.none', language) || '없음'}</option>
                    <option value="DAILY">{t('plan.frequency.daily', language)}</option>
                    <option value="WEEKLY">{t('plan.frequency.weekly', language)}</option>
                    <option value="MONTHLY">{t('plan.frequency.monthly', language)}</option>
                  </select>
                </div>
                {createPlanForm.frequency === 'WEEKLY' && (
                  <div className="goal-detail__form-group">
                    <label>{t('goalDetail.frequencyDay', language) || '요일 선택'}</label>
                    <select
                      value={createPlanForm.frequencyDay || ''}
                      onChange={(e) =>
                        setCreatePlanForm({
                          ...createPlanForm,
                          frequencyDay: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      className="goal-detail__select"
                    >
                      <option value="">{t('calendar.selectDay', language) || '요일을 선택하세요'}</option>
                      <option value="1">{t('calendar.monday', language) || '월요일'}</option>
                      <option value="2">{t('calendar.tuesday', language) || '화요일'}</option>
                      <option value="3">{t('calendar.wednesday', language) || '수요일'}</option>
                      <option value="4">{t('calendar.thursday', language) || '목요일'}</option>
                      <option value="5">{t('calendar.friday', language) || '금요일'}</option>
                      <option value="6">{t('calendar.saturday', language) || '토요일'}</option>
                      <option value="7">{t('calendar.sunday', language) || '일요일'}</option>
                    </select>
                  </div>
                )}
                {createPlanForm.frequency === 'MONTHLY' && (
                  <div className="goal-detail__form-group">
                    <label>{t('goalDetail.frequencyDay', language) || '일 선택'}</label>
                    <select
                      value={createPlanForm.frequencyDay || ''}
                      onChange={(e) =>
                        setCreatePlanForm({
                          ...createPlanForm,
                          frequencyDay: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      className="goal-detail__select"
                    >
                      <option value="">{t('calendar.selectDay', language) || '일을 선택하세요'}</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day}>
                          {day}{language === 'ko' ? '일' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="goal-detail__form-row goal-detail__form-row--dates">
                <Input
                  label={t('goalDetail.startDate', language)}
                  type="date"
                  value={createPlanForm.startDate}
                  onChange={(e) => {
                    const newStartDate = e.target.value
                    setCreatePlanForm({
                      ...createPlanForm,
                      startDate: newStartDate,
                      // NONE이면 종료일도 시작일과 동일하게 업데이트
                      endDate: createPlanForm.frequency === 'NONE' ? newStartDate : createPlanForm.endDate,
                    })
                  }}
                />
                <Input
                  label={t('goalDetail.endDate', language)}
                  type="date"
                  value={createPlanForm.endDate}
                  onChange={(e) =>
                    setCreatePlanForm({ ...createPlanForm, endDate: e.target.value })
                  }
                  readOnly={createPlanForm.frequency === 'NONE'}
                />
              </div>
              <div className="goal-detail__modal-actions">
                <Button variant="primary" onClick={handleUpdatePlan} disabled={isUpdatingPlan || isFetchingPlan}>
                  {(isUpdatingPlan || isFetchingPlan) ? (
                    <>
                      <span className="goal-detail__loading-spinner"></span>
                      {t('messages.processing', language) || '처리중입니다...'}
                    </>
                  ) : (
                    t('common.save', language) || t('common.update', language) || '저장'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* 반복 이체 스케줄 모달 */}
      <Modal
        isOpen={isTransferScheduleModalOpen}
        onClose={() => {
          setIsTransferScheduleModalOpen(false)
          setSelectedAccount(null)
        }}
        title={t('goalDetail.transferScheduleTitle', language)}
        size="md"
      >
        <div className="goal-detail__modal-content">
          {selectedAccount && (
            <>
              <p>{t('goalDetail.transferScheduleDesc', language)}: {selectedAccount.accountName}</p>
              <Input
                label={t('goalDetail.transferAmount', language)}
                type="number"
                value={transferSchedule.amount}
                onChange={(e) =>
                  setTransferSchedule({
                    ...transferSchedule,
                    amount: parseInt(e.target.value) || 0,
                  })
                }
                placeholder={t('goalDetail.transferAmountPlaceholder', language)}
              />
              <div className="goal-detail__form-group">
                <label>{t('goalDetail.frequency', language)}</label>
                <select
                  value={transferSchedule.frequency}
                  onChange={(e) =>
                    setTransferSchedule({
                      ...transferSchedule,
                      frequency: e.target.value as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY',
                    })
                  }
                >
                  <option value="DAILY">{t('plan.frequency.daily', language)}</option>
                  <option value="WEEKLY">{t('plan.frequency.weekly', language)}</option>
                  <option value="MONTHLY">{t('plan.frequency.monthly', language)}</option>
                  <option value="YEARLY">{t('plan.frequency.yearly', language)}</option>
                </select>
              </div>
              {transferSchedule.frequency !== 'DAILY' && (
                <Input
                  label={t('goalDetail.frequencyDay', language)}
                  type="number"
                  value={transferSchedule.frequencyDay}
                  onChange={(e) =>
                    setTransferSchedule({
                      ...transferSchedule,
                      frequencyDay: parseInt(e.target.value) || 1,
                    })
                  }
                  min={1}
                  max={transferSchedule.frequency === 'WEEKLY' ? 6 : transferSchedule.frequency === 'MONTHLY' ? 31 : 365}
                />
              )}
              <div className="goal-detail__form-row goal-detail__form-row--dates">
                <Input
                  label={t('goalDetail.startDate', language)}
                  type="date"
                  value={transferSchedule.startDate}
                  onChange={(e) =>
                    setTransferSchedule({
                      ...transferSchedule,
                      startDate: e.target.value,
                    })
                  }
                />
                <Input
                  label={t('goalDetail.endDate', language)}
                  type="date"
                  value={transferSchedule.endDate}
                  onChange={(e) =>
                    setTransferSchedule({
                      ...transferSchedule,
                      endDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="goal-detail__modal-actions">
                <Button
                  onClick={async () => {
                    if (!selectedAccount || transferSchedule.amount <= 0) {
                      showToast(t('goalDetail.transferScheduleError', language), 'error')
                      return
                    }
                    // 반복 이체 스케줄 생성 로직
                    // 실제로는 백엔드에 스케줄을 저장하거나, 프론트엔드에서 주기적으로 실행
                    // 여기서는 예시로 첫 입금만 실행
                    try {
                      await deposit(dreamId!, selectedAccount.accountId, { amount: transferSchedule.amount })
                      showToast(t('goalDetail.transferScheduleCreated', language), 'success')
                      setIsTransferScheduleModalOpen(false)
                      setSelectedAccount(null)
                      await fetchAccounts(dreamId!)
                    } catch (error) {
                      showToast(t('goalDetail.transferScheduleFailed', language), 'error')
                    }
                  }}
                >
                  {t('common.save', language)}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* 아이템(체크리스트) 모달 */}
      <Modal
        isOpen={isChecklistModalOpen}
        onClose={handleChecklistClose}
        title=""
        size="lg"
      >
        {dreamId != null && checklistPlanId != null && (
          <Checklist
            dreamIdProp={dreamId}
            planIdProp={checklistPlanId}
            isModal
            onClose={handleChecklistClose}
            onItemUpdated={() => {
              if (dreamId && checklistPlanId) {
                fetchItems(dreamId, checklistPlanId).catch(() => {})
                if (currentGoalId) fetchPlans(dreamId, currentGoalId).catch(() => {})
                fetchDreamById(dreamId).catch(() => {})
              }
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default GoalDetail
